import { z } from 'zod';
import { moduleIdSchema } from './refs.js';

export const NODE_SHAPES = ['circle', 'square', 'diamond', 'hexagon', 'triangle', 'ring', 'star'] as const;
export type NodeShape = (typeof NODE_SHAPES)[number];

const typeKeyPattern = /^[a-z][a-z0-9-]{1,39}:[a-z][a-z0-9_]{1,39}$/;
const capabilityPattern = /^[a-z][a-zA-Z0-9.]+@\d+$/;

export const entityTypeDefinitionSchema = z.object({
  typeKey: z.string().regex(typeKeyPattern),
  label: z.string().min(1),
  pluralLabel: z.string().min(1),
  appearance: z.object({
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    shape: z.enum(NODE_SHAPES),
  }),
  /** Browser route for the owning module; `:id` is replaced by the entity id. */
  routeTemplate: z.string().startsWith('/'),
});
export type EntityTypeDefinition = z.infer<typeof entityTypeDefinitionSchema>;

export const predicateDefinitionSchema = z.object({
  id: z.string().regex(typeKeyPattern),
  label: z.string().min(1),
  inverseLabel: z.string().min(1),
  directed: z.boolean(),
  from: z.union([z.literal('*'), z.array(z.string().regex(typeKeyPattern)).min(1)]),
  to: z.union([z.literal('*'), z.array(z.string().regex(typeKeyPattern)).min(1)]),
  /** Whether people may author this predicate by hand (otherwise projection-only). */
  allowManual: z.boolean(),
  allowSelf: z.boolean().default(false),
});
export type PredicateDefinition = z.input<typeof predicateDefinitionSchema>;

export const CONTRIBUTIONS = ['navigation', 'search', 'home', 'entityPreviews', 'graph', 'export', 'quickCapture'] as const;

export const moduleManifestSchema = z.object({
  id: moduleIdSchema,
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  platformContract: z.literal('1'),
  name: z.string().min(1),
  description: z.string(),
  navigation: z
    .object({ label: z.string(), path: z.string().startsWith('/'), order: z.number().int() })
    .optional(),
  routes: z.array(z.string().startsWith('/')),
  collections: z.array(z.string().regex(/^[a-z]+_[a-z_]+$/)),
  entityTypes: z.array(entityTypeDefinitionSchema),
  predicates: z.array(predicateDefinitionSchema),
  publicCapabilities: z.array(z.string().regex(capabilityPattern)),
  requires: z.array(z.string().regex(capabilityPattern)),
  optional: z.array(z.string().regex(capabilityPattern)),
  contributions: z.array(z.enum(CONTRIBUTIONS)),
  events: z.object({ emits: z.array(z.string()), consumes: z.array(z.string()) }),
  /** Other modules that must be enabled for this module to be enabled. */
  requiresModules: z.array(moduleIdSchema).default([]),
  /** Development/test fixture: never registered in production builds. */
  devOnly: z.boolean().default(false),
});
export type ModuleManifest = z.input<typeof moduleManifestSchema>;
export type ParsedModuleManifest = z.output<typeof moduleManifestSchema>;

export function defineManifest(manifest: ModuleManifest): ParsedModuleManifest {
  const parsed = moduleManifestSchema.parse(manifest);
  const prefix = `${parsed.id}:`;
  for (const t of parsed.entityTypes) {
    if (!t.typeKey.startsWith(prefix)) throw new Error(`${parsed.id} cannot declare entity type ${t.typeKey}`);
  }
  for (const c of parsed.collections) {
    const collectionPrefix = parsed.id === 'core' ? 'core_' : `${parsed.id.split('-')[0]}_`;
    if (!c.startsWith(collectionPrefix)) throw new Error(`${parsed.id} collection ${c} must start with ${collectionPrefix}`);
  }
  return parsed;
}

export function resolveRoute(template: string, id: string): string {
  return template.replace(':id', encodeURIComponent(id));
}
